<script setup lang="ts">
import VueApexCharts from 'vue3-apexcharts'

import {
  IAlarmAdherenceByShift,
  IAlarmByClassification,
  IAlarmByGroup,
  IAlarmByNature,
  IAlarmBySensor,
  IAlarmByShift,
  IAlarmByType,
  IClassificationMetrics,
  IAlarmDurationByType,
} from '@/interfaces/stores/alarm'

import { formatAlarmReportChartParams } from '@/utils/components/alarm/alarm-report-charts'

import { computed, onMounted, ref, watch } from 'vue'

type ChartReadings =
  | IAlarmBySensor
  | IAlarmByGroup
  | IAlarmByType
  | IAlarmByClassification
  | IClassificationMetrics
  | IAlarmDurationByType
  | IAlarmByShift
  | IAlarmByNature
  | IAlarmAdherenceByShift

type ApexChartKind = 'bar' | 'donut' | 'polarArea'

const props = withDefaults(
  defineProps<{
    chartTitle: string
    chartType: string
    readings: ChartReadings[]
    compact?: boolean
  }>(),
  { compact: true },
)

const chartLoaded = ref(false)
const chartOptions = ref<object>({})
const chartHeight = ref(340)
const apexChartType = ref<ApexChartKind>('donut')
const series = ref<unknown[]>([])

const isDonutChart = computed(() => apexChartType.value !== 'bar')

const hasData = computed(() => chartLoaded.value && seriesHasValues(series.value))

function seriesHasValues(seriesParams: unknown[]): boolean {
  if (!seriesParams.length) return false

  const first = seriesParams[0]
  if (typeof first === 'number') return true

  if (first && typeof first === 'object' && 'data' in first) {
    const data = (first as { data: unknown[] }).data
    return Array.isArray(data) && data.length > 0
  }

  return true
}

function initializeChart(readings: ChartReadings[]) {
  const {
    seriesParams,
    chartOptionsParams,
    chartHeight: height,
    apexChartType: kind,
  } = formatAlarmReportChartParams(props.chartType, readings, props.compact)

  series.value = seriesParams
  chartOptions.value = chartOptionsParams
  chartHeight.value = height ?? (kind === 'bar' ? 260 : 340)
  apexChartType.value = (kind as ApexChartKind) ?? 'donut'
  chartLoaded.value = true
}

watch(
  () => props.readings,
  (newValue) => {
    initializeChart(newValue)
  },
)

onMounted(() => {
  initializeChart(props.readings)
})
</script>

<template>
  <article v-if="hasData" class="flex h-full min-h-0 flex-col border border-stroke bg-white shadow-sm">
    <header class="shrink-0 border-b border-stroke px-4 py-3">
      <h3 class="font-satoshi text-sm font-bold leading-snug text-black">
        {{ chartTitle }}
      </h3>
    </header>

    <div
      class="flex w-full min-w-0 flex-1 items-center justify-center overflow-hidden"
      :class="isDonutChart ? 'px-4 py-4' : 'px-2 py-2'">
      <VueApexCharts
        :key="`${chartType}-${apexChartType}`"
        class="w-full max-w-full"
        :type="apexChartType"
        :height="chartHeight"
        width="100%"
        :options="chartOptions"
        :series="series" />
    </div>
  </article>
</template>
